import React, { useState, useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Info, Sparkles, Copy, Send, Trash2, X, ShieldAlert, Settings } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import { useAuth } from '../../context/AuthContext';
import {
  getShiftScheduleGrid,
  generateShiftSchedules,
  getShiftScheduleGenerateScope,
  publishShiftSchedules,
  clearShiftSchedules,
  copyWeekShiftSchedules,
  createShiftSchedule,
  updateShiftSchedule,
  getShiftScheduleCompliance,
} from '../../services/shiftScheduleService';
import api from '../../services/api';

import Button from '../../components/ui/Button';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import { buildReportLabels } from '../../utils/reportLabels';
import PersonCell from '../../components/ui/PersonCell';
import Tooltip from '../../components/ui/Tooltip';
import DateTimePicker from '../../components/ui/DateTimePicker';
import CheckboxInput from '../../components/ui/CheckboxInput';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import TextInput from '../../components/ui/TextInput';
import WorkHourPickerDrawer from './WorkHourPickerDrawer';
import ComplianceDrawer from './ComplianceDrawer';
import ComplianceSettingsModal from './ComplianceSettingsModal';

// ─── Helpers ──────────────────────────────────────────────────────

const getMonday = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  date.setHours(0, 0, 0, 0);
  return date;
};

const addDays = (d, n) => {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
};

const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatDateShort = (d) => {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}`;
};

const formatDateFull = (d) => {
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatISODisplay = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

const DAY_KEYS_SHORT = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Uyum ihlali şiddet renkleri — ComplianceDrawer.js ile birebir (backend
// error/warning/info enum'u). Hücre rozetinin rengini belirlemek için en
// yüksek şiddeti taşıyan ihlali bulur.
const COMPLIANCE_SEVERITY_COLORS = { error: '#E53935', warning: '#F59E0B', info: '#0291EF' };
const COMPLIANCE_SEVERITY_RANK = { error: 3, warning: 2, info: 1 };

const getHighestComplianceSeverityColor = (list) => {
  if (!list || !list.length) return COMPLIANCE_SEVERITY_COLORS.info;
  let top = list[0];
  list.forEach((v) => {
    if ((COMPLIANCE_SEVERITY_RANK[v.severity] || 0) > (COMPLIANCE_SEVERITY_RANK[top.severity] || 0)) top = v;
  });
  return COMPLIANCE_SEVERITY_COLORS[top.severity] || COMPLIANCE_SEVERITY_COLORS.info;
};

// ─── Styled Components ───────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  flex-wrap: wrap;
  gap: 12px;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid ${({ theme }) => theme.colors.border?.main || '#E0E4E8'};
  border-radius: 4px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgColor};
  }
`;

const DateRangeLabel = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 200px;
  text-align: center;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const GridWrapper = styled.div`
  flex: 1;
  overflow: auto;
  border: 1px solid #E8ECF0;
  border-radius: 6px;
  background: #fff;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 220px repeat(7, minmax(130px, 1fr));
  min-width: 1140px;
`;

const HeaderCell = styled.div`
  padding: 12px 16px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
  background: ${({ theme }) => theme.colors.bgColor};
  border-bottom: 2px solid #E8ECF0;
  text-align: center;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 3;

  ${({ $isToday }) => $isToday && `
    color: #0291EF;
  `}

  &:first-child {
    text-align: left;
    left: 0;
    z-index: 4;
  }
`;

const EmployeeCell = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #fff;
  border-bottom: 1px solid #F1F5F9;
  border-right: 1px solid #E8ECF0;
`;

const RotationBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(2, 145, 239, 0.08);
  border: 1px solid rgba(2, 145, 239, 0.25);
  color: #0277BD;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.4;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NoRotationLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #B6BEC9;
`;

const ResultBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 10px;
  margin-bottom: 4px;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-family: ${({ theme }) => theme.metrics.font.family};
  line-height: 1.55;
  box-shadow: 0 2px 10px rgba(0, 66, 66, 0.05);
  ${({ $variant }) => ($variant === 'success' ? `
    background: rgba(2, 211, 191, 0.07);
    border: 1px solid rgba(2, 211, 191, 0.35);
    color: #00695C;
  ` : `
    background: rgba(255, 152, 0, 0.07);
    border: 1px solid rgba(255, 152, 0, 0.35);
    color: #9A5B00;
  `)}
`;

const BannerIcon = styled.span`
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  ${({ $variant }) => ($variant === 'success' ? `
    background: rgba(2, 211, 191, 0.16);
    color: #009688;
  ` : `
    background: rgba(255, 152, 0, 0.16);
    color: #E65100;
  `)}
`;

const BannerText = styled.div`
  flex: 1;
`;

const BannerClose = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  color: inherit;
  padding: 2px;
  opacity: 0.7;
  &:hover { opacity: 1; }
`;

const ScopeInfoBox = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 6px;
  background: rgba(2, 145, 239, 0.06);
  border: 1px solid rgba(2, 145, 239, 0.2);
  color: #075985;
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-family: ${({ theme }) => theme.metrics.font.family};
  line-height: 1.5;
`;

const AdvancedToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.primary?.main || '#0291EF'};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const ShiftCell = styled.div`
  position: relative;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #F1F5F9;
  border-right: 1px solid #F1F5F9;
  min-height: 64px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $isToday }) => $isToday && `
    background: rgba(2, 145, 239, 0.03);
  `}

  ${({ $clickable }) => $clickable && `
    &:hover {
      background: rgba(2, 145, 239, 0.06);
    }
  `}

  &:last-child {
    border-right: none;
  }
`;

const ViolationBadgeAnchor = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 1;
`;

const ViolationDot = styled.span`
  display: block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  border: 1.5px solid #fff;
  box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.15);
`;

const ShiftCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 6px;
  border-radius: 6px;
  background: ${({ $color }) => $color ? `${$color}26` : '#F1F5F9'};
  border: ${({ $isDraft }) => $isDraft ? '1.5px dashed' : '1.5px solid'};
  border-color: ${({ $color }) => $color ? `${$color}60` : '#E2E8F0'};
  cursor: default;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const ShiftCode = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ $color, theme }) => $color || theme.colors.text.primary};
  line-height: 1.2;
`;

const ShiftTime = styled.span`
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.regular};
  color: ${({ theme }) => theme.colors.text.secondary || '#64748B'};
  line-height: 1.3;
  margin-top: 2px;
`;

const OffCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 8px 6px;
  border-radius: 6px;
  background: #F1F5F9;
  border: 1.5px solid #E2E8F0;
  color: #94A3B8;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  text-align: center;
  line-height: 1.2;
`;

const ContextCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: 100%;
  padding: 6px;
  border-radius: 6px;
  text-align: center;
  ${({ $kind }) => {
    if ($kind === 'leaveApproved') return `
      background: rgba(156, 39, 176, 0.12);
      border: 1.5px solid rgba(156, 39, 176, 0.45);
      color: #6A1B9A;
    `;
    if ($kind === 'leavePending') return `
      background: rgba(255, 152, 0, 0.12);
      border: 1.5px dashed rgba(255, 152, 0, 0.55);
      color: #E65100;
    `;
    if ($kind === 'holiday') return `
      background: rgba(96, 125, 139, 0.10);
      border: 1.5px solid rgba(96, 125, 139, 0.4);
      color: #455A64;
    `;
    return '';
  }}
`;

const ContextLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const ContextSubLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  opacity: 0.85;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: 1 / -1;
  padding: 60px 24px;
  color: ${({ theme }) => theme.colors.text.secondary || '#94A3B8'};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  grid-column: 1 / -1;
  padding: 80px 24px;
  color: ${({ theme }) => theme.colors.text.secondary || '#94A3B8'};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
`;

// ─── Drawer styled components ────────────────────────────────────

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;

const DrawerTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${({ theme }) => theme.colors.text.dark?.little || '#A0A8B4'};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const DrawerFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #E8ECF0;
`;

const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 18px;
`;

const FieldItem = styled.div`
  grid-column: span ${({ $span }) => $span || 12};
`;

// Kompakt filtre çubuğu: kontroller tüm genişliğe yayılmaz, sola yaslı ve
// alçak (34px) durur. İçindeki TextInput da select'lerle aynı boya çekilir.
const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 0 0 12px 0;

  & > * {
    flex: 0 0 220px;
    max-width: 220px;
  }

  input {
    padding: 7px 12px;
    font-size: 13px;
  }
`;

const PagerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 0 0 0;
`;

const PagerText = styled.span`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid #E0E4E8;
  background: #fff;
  color: #475569;

  &:hover:not(:disabled) {
    border-color: #0291EF;
    color: #0291EF;
    background: rgba(2, 145, 239, 0.06);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ $variant }) => $variant === 'primary' && `
    background: #0291EF;
    border-color: #0291EF;
    color: #fff;
    &:hover:not(:disabled) {
      background: #0277BD;
      border-color: #0277BD;
      color: #fff;
    }
  `}

  ${({ $variant }) => $variant === 'danger' && `
    &:hover:not(:disabled) {
      border-color: #E53935;
      color: #E53935;
      background: rgba(229, 57, 53, 0.06);
    }
  `}
`;

// ─── Main Component ──────────────────────────────────────────────

export default function ShiftSchedulesPage() {
  const { t } = useTranslation();
  const { canRead, canWrite } = useAuth();

  // Week navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(() => getMonday(new Date()));
  const [gridData, setGridData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Grid pagination — büyük tenant'ta tüm çalışan satırlarını tek seferde
  // indirip DOM'a basmamak için sayfa başına sabit kullanıcı sayısı.
  const GRID_PAGE_SIZE = 50;
  const [gridPage, setGridPage] = useState(1);
  const [gridTotal, setGridTotal] = useState(0);

  // Drawer states
  const [generateDrawerOpen, setGenerateDrawerOpen] = useState(false);
  const [clearDrawerOpen, setClearDrawerOpen] = useState(false);

  // Compliance check state (Denetle)
  const [complianceOpen, setComplianceOpen] = useState(false);
  const [complianceViolations, setComplianceViolations] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [violationsByCell, setViolationsByCell] = useState({});

  // Compliance settings modal (Uyum Kuralları — yalnız SM.Write)
  const [complianceSettingsOpen, setComplianceSettingsOpen] = useState(false);

  // Confirm states
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [copyConfirm, setCopyConfirm] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [copying, setCopying] = useState(false);

  // Generate form state
  const [generateForm, setGenerateForm] = useState({
    startDate: null,
    endDate: null,
    overwriteExisting: false,
    employeeIds: [],
    departmentIds: [],
    locationIds: [],
    userGroupIds: [],
  });
  const [generating, setGenerating] = useState(false);
  const [scopeInfo, setScopeInfo] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);

  // Clear form state
  const [clearForm, setClearForm] = useState({
    startDate: null,
    endDate: null,
    employeeIds: [],
    departmentIds: [],
    locationIds: [],
    userGroupIds: [],
  });
  const [clearing, setClearing] = useState(false);

  // Day-cell picker state
  const [pickerCtx, setPickerCtx] = useState(null);
  const [pickerSaving, setPickerSaving] = useState(false);

  // WorkHour catalog (fetched once on mount, shared with picker drawer)
  const [workHours, setWorkHours] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.get('/v1/work-hours', { params: { pageSize: 100 } })
      .then((res) => {
        if (cancelled) return;
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setWorkHours(items);
      })
      .catch(() => { if (!cancelled) setWorkHours([]); });
    return () => { cancelled = true; };
  }, []);

  // Filter state
  const [filterNameInput, setFilterNameInput] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterDepartmentIds, setFilterDepartmentIds] = useState([]);
  const [filterUserGroupIds, setFilterUserGroupIds] = useState([]);

  // Debounce name input → applied filter
  useEffect(() => {
    const t = setTimeout(() => setFilterName(filterNameInput.trim()), 350);
    return () => clearTimeout(t);
  }, [filterNameInput]);

  // Computed dates
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  }, [currentWeekStart]);

  const weekEnd = useMemo(() => addDays(currentWeekStart, 6), [currentWeekStart]);

  const todayStr = useMemo(() => formatDateISO(new Date()), []);

  // ─── Data fetching ──────────────────────────────────────────────

  const fetchGrid = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        startDate: formatDateISO(currentWeekStart),
        endDate: formatDateISO(addDays(currentWeekStart, 6)),
        pageNumber: gridPage,
        pageSize: GRID_PAGE_SIZE,
      };
      if (filterName) params.name = filterName;
      if (filterDepartmentIds.length) params.departmentIds = filterDepartmentIds.join(',');
      if (filterUserGroupIds.length) params.userGroupIds = filterUserGroupIds.join(',');

      const res = await getShiftScheduleGrid(params);
      // pageSize gönderildiğinde backend PaginatedList döner; eski düz-liste
      // şekline de tolerans (repo standardı parse deseni).
      const result = res.data?.data;
      if (Array.isArray(result)) {
        setGridData(result);
        setGridTotal(result.length);
      } else {
        setGridData(result?.data || []);
        setGridTotal(result?.totalRowCount || 0);
      }
    } catch {
      setGridData([]);
      setGridTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart, filterName, filterDepartmentIds, filterUserGroupIds, gridPage]);

  useEffect(() => {
    fetchGrid();
  }, [fetchGrid]);

  // Filtre değişince ilk sayfaya dön; hafta gezinmede sayfa KORUNUR
  // (kullanıcı aynı ekibin başka haftasına bakıyor).
  useEffect(() => {
    setGridPage(1);
  }, [filterName, filterDepartmentIds, filterUserGroupIds]);

  // ─── Navigation ─────────────────────────────────────────────────

  const handlePrevWeek = useCallback(() => {
    setCurrentWeekStart((prev) => addDays(prev, -7));
  }, []);

  const handleNextWeek = useCallback(() => {
    setCurrentWeekStart((prev) => addDays(prev, 7));
  }, []);

  const handleToday = useCallback(() => {
    setCurrentWeekStart(getMonday(new Date()));
  }, []);

  // ─── Action: Publish ───────────────────────────────────────────

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    try {
      await publishShiftSchedules({
        startDate: formatDateISO(currentWeekStart),
        endDate: formatDateISO(addDays(currentWeekStart, 6)),
      });
      await fetchGrid();
    } finally {
      setPublishing(false);
      setPublishConfirm(false);
    }
  }, [currentWeekStart, fetchGrid]);

  // ─── Action: Copy Previous Week ────────────────────────────────

  const handleCopy = useCallback(async () => {
    setCopying(true);
    try {
      await copyWeekShiftSchedules({
        sourceStartDate: formatDateISO(addDays(currentWeekStart, -7)),
        targetStartDate: formatDateISO(currentWeekStart),
      });
      await fetchGrid();
    } finally {
      setCopying(false);
      setCopyConfirm(false);
    }
  }, [currentWeekStart, fetchGrid]);

  // ─── Dışa aktarma (Excel / PDF) ─────────────────────────────────
  // AKM POC §7.1 — görünen haftanın TAMAMI (sayfa değil) ve çizelgenin süzgeçleri; eski CSV kalktı. Hücre ve gün adları
  // web dilinde gider (izin, tatil, fazla mesai eki, kısa gün adları).

  const gridExportParams = useMemo(() => {
    const params = {
      startDate: formatDateISO(currentWeekStart),
      endDate: formatDateISO(addDays(currentWeekStart, 6)),
    };
    if (filterName) params.name = filterName;
    if (filterDepartmentIds.length) params.departmentIds = filterDepartmentIds.join(',');
    if (filterUserGroupIds.length) params.userGroupIds = filterUserGroupIds.join(',');
    return params;
  }, [currentWeekStart, filterName, filterDepartmentIds, filterUserGroupIds]);

  const getGridExportLabels = useCallback(() => {
    const leave = t('shiftSchedule.leave.label');
    return buildReportLabels(t, [], t('reportExport.reports.sm.shiftSchedule'), {
      userFullName: t('shiftSchedule.employee'),
      'weekdayShort.0': t('shiftSchedule.daysShort.sun'),
      'weekdayShort.1': t('shiftSchedule.daysShort.mon'),
      'weekdayShort.2': t('shiftSchedule.daysShort.tue'),
      'weekdayShort.3': t('shiftSchedule.daysShort.wed'),
      'weekdayShort.4': t('shiftSchedule.daysShort.thu'),
      'weekdayShort.5': t('shiftSchedule.daysShort.fri'),
      'weekdayShort.6': t('shiftSchedule.daysShort.sat'),
      'grid.holiday': t('shiftSchedule.holiday'),
      'grid.leave': leave,
      'grid.leavePending': `${leave} (${t('shiftSchedule.leave.pending')})`,
      'grid.overtimeSuffix': t('shiftSchedule.overtime'),
    });
  }, [t]);

  // ─── Action: Generate ──────────────────────────────────────────

  const handleOpenGenerate = useCallback(() => {
    setGenerateForm({
      startDate: formatDateISO(currentWeekStart),
      endDate: formatDateISO(addDays(currentWeekStart, 6)),
      overwriteExisting: false,
      employeeIds: [],
      departmentIds: [],
      locationIds: [],
      userGroupIds: [],
    });
    setAdvancedOpen(false);
    setScopeInfo(null);
    getShiftScheduleGenerateScope()
      .then((res) => setScopeInfo(res.data?.data || null))
      .catch(() => setScopeInfo(null));
    setGenerateDrawerOpen(true);
  }, [currentWeekStart]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await generateShiftSchedules(generateForm);
      const data = res.data?.data || {};
      const result = {
        generatedCount: data.generatedCount ?? 0,
        skippedExisting: data.skippedExisting ?? 0,
        startDate: data.startDate || generateForm.startDate,
        endDate: data.endDate || generateForm.endDate,
        complianceSummary: data.complianceSummary || null,
      };
      setGenerateResult(result);
      setGenerateDrawerOpen(false);
      if (result.generatedCount > 0 && result.startDate) {
        // Üretilen aralığın ilk haftasına git — "üretti ama ekranda görünmüyor"
        // tuzağını kapatır (hafta değişimi grid'i otomatik yeniler).
        setCurrentWeekStart(getMonday(new Date(`${result.startDate}T00:00:00`)));
      } else {
        await fetchGrid();
      }
    } finally {
      setGenerating(false);
    }
  }, [generateForm, fetchGrid]);

  // ─── Action: Clear ─────────────────────────────────────────────

  const handleOpenClear = useCallback(() => {
    setClearForm({
      startDate: formatDateISO(currentWeekStart),
      endDate: formatDateISO(addDays(currentWeekStart, 6)),
      employeeIds: [],
      departmentIds: [],
      locationIds: [],
      userGroupIds: [],
    });
    setClearDrawerOpen(true);
  }, [currentWeekStart]);

  const handleClear = useCallback(async () => {
    setClearing(true);
    try {
      await clearShiftSchedules(clearForm);
      setClearDrawerOpen(false);
      await fetchGrid();
    } finally {
      setClearing(false);
    }
  }, [clearForm, fetchGrid]);

  // ─── Action: Compliance Check (Denetle) ─────────────────────────

  const handleCheckCompliance = useCallback(async () => {
    setComplianceLoading(true);
    setComplianceOpen(true);
    try {
      const params = {
        startDate: formatDateISO(currentWeekStart),
        endDate: formatDateISO(addDays(currentWeekStart, 6)),
      };
      if (filterName) params.name = filterName;
      if (filterDepartmentIds.length) params.departmentIds = filterDepartmentIds.join(',');
      if (filterUserGroupIds.length) params.userGroupIds = filterUserGroupIds.join(',');
      const res = await getShiftScheduleCompliance(params);
      const list = res.data?.data || [];
      setComplianceViolations(list);
      const byCell = {};
      list.forEach((v) => {
        const key = `${v.userId}_${v.date}`;
        (byCell[key] = byCell[key] || []).push(v);
      });
      setViolationsByCell(byCell);
    } catch { setComplianceViolations([]); }
    setComplianceLoading(false);
  }, [currentWeekStart, filterName, filterDepartmentIds, filterUserGroupIds]);

  // Hücre rozeti tooltip'i — ComplianceDrawer.js'teki renderMessage ile aynı
  // i18n render mantığı (messageKey + numaralı messageArgs).
  const renderComplianceMessage = useCallback((v) => {
    const opts = {};
    (v.messageArgs || []).forEach((a, i) => { opts[i] = a; });
    return t(v.messageKey, opts);
  }, [t]);

  // ─── AsyncGenericSelect search helpers ──────────────────────────

  const searchEmployees = useCallback(async (query) => {
    try {
      const params = query ? { 'firstName[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchDepartments = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/departments', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchLocations = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/locations', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  const searchUserGroups = useCallback(async (query) => {
    try {
      const params = query ? { 'name[like]': query, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/user-groups', { params });
      const result = res.data?.data;
      return Array.isArray(result) ? result : (result?.data || []);
    } catch { return []; }
  }, []);

  // ─── Day-cell click → open picker ───────────────────────────────

  const handleOpenPicker = useCallback((row, day, shift) => {
    setPickerCtx({
      userId: row.userId,
      userFullName: row.userFullName,
      date: formatDateISO(day),
      scheduleId: shift?.id || null,
      currentWorkHourId: shift?.workHourId || null,
      // Seçicinin onay kutuları hücrenin mevcut değerleriyle başlar (AKM POC §5.1).
      isOvertime: !!shift?.isOvertime,
      isWeeklyRest: !!shift?.isWeeklyRest,
    });
  }, []);

  const handleClosePicker = useCallback(() => {
    if (pickerSaving) return;
    setPickerCtx(null);
  }, [pickerSaving]);

  // Seçicideki "Fazla Mesai Vardiyası" ve "Hafta Tatili" işaretleri seçimle birlikte gelir; eskiden
  // isOvertime hep false SABİTİ gidiyordu (planlı fazla mesai işareti her düzenlemede siliniyordu).
  // Vardiyasız günde fazla mesai işareti anlamsızdır → false.
  const handlePickWorkHour = useCallback(async (workHourId, flags = {}) => {
    if (!pickerCtx) return;
    setPickerSaving(true);
    try {
      const payload = {
        userId: pickerCtx.userId,
        date: pickerCtx.date,
        workHourId: workHourId || null,
        notes: null,
        isOvertime: workHourId ? !!flags.isOvertime : false,
        isWeeklyRest: !!flags.isWeeklyRest,
      };
      if (pickerCtx.scheduleId) {
        await updateShiftSchedule(pickerCtx.scheduleId, payload);
      } else {
        await createShiftSchedule(payload);
      }
      setPickerCtx(null);
      await fetchGrid();
    } catch { /* interceptor */ }
    setPickerSaving(false);
  }, [pickerCtx, fetchGrid]);

  // ─── Format time range ─────────────────────────────────────────

  const formatTimeRange = useCallback((start, end) => {
    if (!start || !end) return '';
    const fmt = (val) => {
      if (typeof val === 'string' && val.includes(':')) {
        return val.substring(0, 5);
      }
      return val;
    };
    return `${fmt(start)}-${fmt(end)}`;
  }, []);

  // ─── Cell tooltip text ─────────────────────────────────────────

  const getCellTooltip = useCallback((shift) => {
    if (!shift) return null;
    const parts = [];
    if (shift.leaveStatus) {
      const label = shift.leaveType || t('shiftSchedule.leave.label');
      const status = shift.leaveStatus === 'approved'
        ? t('shiftSchedule.leave.approved')
        : t('shiftSchedule.leave.pending');
      parts.push(`${label} (${status})`);
      return parts.join(' | ');
    }
    if (shift.isHoliday) {
      parts.push(shift.holidayName || t('shiftSchedule.holiday'));
      return parts.join(' | ');
    }
    if (shift.workHourName) parts.push(shift.workHourName);
    if (shift.start && shift.end) parts.push(`${shift.start} - ${shift.end}`);
    if (shift.status) parts.push(t(`shiftSchedule.status.${shift.status}`));
    if (shift.isOvertime) parts.push(t('shiftSchedule.overtime'));
    if (shift.isWeeklyRest) parts.push(t('shiftSchedule.isWeeklyRest'));
    if (shift.notes) parts.push(shift.notes);
    return parts.length ? parts.join(' | ') : null;
  }, [t]);

  const renderDayContent = useCallback((shift) => {
    if (!shift) return <OffCard>{t('shiftSchedule.off')}</OffCard>;

    const tooltip = getCellTooltip(shift);

    if (shift.leaveStatus) {
      const kind = shift.leaveStatus === 'approved' ? 'leaveApproved' : 'leavePending';
      const subLabel = shift.leaveStatus === 'approved'
        ? t('shiftSchedule.leave.approved')
        : t('shiftSchedule.leave.pending');
      return (
        <Tooltip text={tooltip} position="bottom">
          <ContextCard $kind={kind}>
            <ContextLabel>{shift.leaveType || t('shiftSchedule.leave.label')}</ContextLabel>
            <ContextSubLabel>{subLabel}</ContextSubLabel>
          </ContextCard>
        </Tooltip>
      );
    }

    if (shift.isHoliday) {
      return (
        <Tooltip text={tooltip} position="bottom">
          <ContextCard $kind="holiday">
            <ContextLabel>{t('shiftSchedule.holiday')}</ContextLabel>
          </ContextCard>
        </Tooltip>
      );
    }

    if (shift.workHourId) {
      return (
        <Tooltip text={tooltip} position="bottom">
          <ShiftCard
            $color={shift.workHourColorCode}
            $isDraft={shift.status === 'draft'}
          >
            <ShiftCode $color={shift.workHourColorCode}>{shift.workHourShortCode}</ShiftCode>
            <ShiftTime>{formatTimeRange(shift.start, shift.end)}</ShiftTime>
          </ShiftCard>
        </Tooltip>
      );
    }

    // Vardiya yönetimindeki kişinin yayınlanmış vardiyası olmayan günü = izin günü (puantajla aynı kural).
    if (shift.source === 'managedOff') {
      return <OffCard data-source="managedOff">{t('shiftSchedule.managedOff')}</OffCard>;
    }
    if (shift.isWeeklyRest) return <OffCard>{t('shiftSchedule.isWeeklyRest')}</OffCard>;

    return <OffCard>{t('shiftSchedule.off')}</OffCard>;
  }, [t, getCellTooltip, formatTimeRange]);

  // ─── Day header label ──────────────────────────────────────────

  const getDayLabel = useCallback((date, index) => {
    const dayKey = DAY_KEYS_SHORT[index];
    const dayName = t(`shiftSchedule.daysShort.${dayKey}`);
    const dayNum = String(date.getDate()).padStart(2, '0');
    return `${dayName} ${dayNum}`;
  }, [t]);

  // ─── Render ─────────────────────────────────────────────────────

  const hasWriteAccess = canWrite('SM');
  // Day-cell editing is allowed for any SM.Read user — backend additionally
  // enforces that the target user is a subordinate (admins bypass).
  const canEditCell = canRead('SM');

  return (
    <Container>
      {/* Toolbar */}
      <Toolbar>
        <NavGroup>
          <NavButton onClick={handlePrevWeek} aria-label={t('shiftSchedule.prevWeek')}>
            <ChevronLeft size={18} />
          </NavButton>
          <NavButton onClick={handleNextWeek} aria-label={t('shiftSchedule.nextWeek')}>
            <ChevronRight size={18} />
          </NavButton>
          <DateRangeLabel>
            {formatDateFull(currentWeekStart)} - {formatDateFull(weekEnd)}
          </DateRangeLabel>
          <Button variant="ghost" onClick={handleToday}>
            {t('shiftSchedule.today')}
          </Button>
        </NavGroup>

        <ActionGroup>
          <ReportExportButtons
            url="/v1/shift-schedules/grid/export"
            params={gridExportParams}
            getLabels={getGridExportLabels}
          />
          {canRead('SM') && (
            <Tooltip text={t('shiftSchedule.checkCompliance')} position="bottom">
              <IconBtn onClick={handleCheckCompliance} aria-label={t('shiftSchedule.checkCompliance')}>
                <ShieldAlert size={18} />
              </IconBtn>
            </Tooltip>
          )}
          {hasWriteAccess && (
            <>
              <Tooltip text={t('shiftCompliance.settings.title')} position="bottom">
                <IconBtn onClick={() => setComplianceSettingsOpen(true)} aria-label={t('shiftCompliance.settings.title')}>
                  <Settings size={18} />
                </IconBtn>
              </Tooltip>
              <Tooltip text={t('shiftSchedule.generate')} position="bottom">
                <IconBtn onClick={handleOpenGenerate} aria-label={t('shiftSchedule.generate')}>
                  <Sparkles size={18} />
                </IconBtn>
              </Tooltip>
              <Tooltip text={t('shiftSchedule.copy')} position="bottom">
                <IconBtn onClick={() => setCopyConfirm(true)} aria-label={t('shiftSchedule.copy')}>
                  <Copy size={18} />
                </IconBtn>
              </Tooltip>
              <Tooltip text={t('shiftSchedule.publish')} position="bottom">
                <IconBtn $variant="primary" onClick={() => setPublishConfirm(true)} aria-label={t('shiftSchedule.publish')}>
                  <Send size={18} />
                </IconBtn>
              </Tooltip>
              <Tooltip text={t('shiftSchedule.clear')} position="bottom">
                <IconBtn $variant="danger" onClick={handleOpenClear} aria-label={t('shiftSchedule.clear')}>
                  <Trash2 size={18} />
                </IconBtn>
              </Tooltip>
            </>
          )}
        </ActionGroup>
      </Toolbar>

      {/* Üretim sonucu banner'ı */}
      {generateResult && (
        <ResultBanner $variant={generateResult.generatedCount > 0 ? 'success' : 'warning'}>
          <BannerIcon $variant={generateResult.generatedCount > 0 ? 'success' : 'warning'}>
            {generateResult.generatedCount > 0 ? <CheckCircle2 size={16} /> : <Info size={16} />}
          </BannerIcon>
          <BannerText>
            {generateResult.generatedCount > 0 ? (
              <>
                {t('shiftSchedule.generateResult.success', {
                  count: generateResult.generatedCount,
                  start: formatISODisplay(generateResult.startDate),
                  end: formatISODisplay(generateResult.endDate),
                })}
                {generateResult.skippedExisting > 0 && (
                  <>{' '}{t('shiftSchedule.generateResult.skippedInfo', { count: generateResult.skippedExisting })}</>
                )}
              </>
            ) : generateResult.skippedExisting > 0
              ? t('shiftSchedule.generateResult.zeroSkipped', { count: generateResult.skippedExisting })
              : t('shiftSchedule.generateResult.zeroEmpty')}
            {generateResult.complianceSummary &&
              (generateResult.complianceSummary.errorCount + generateResult.complianceSummary.warningCount + generateResult.complianceSummary.infoCount) > 0 && (
                <>{' '}
                  <button
                    type="button"
                    onClick={handleCheckCompliance}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      fontWeight: 700,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {t('shiftSchedule.generateResult.complianceHint', {
                      errors: generateResult.complianceSummary.errorCount,
                      warnings: generateResult.complianceSummary.warningCount,
                      infos: generateResult.complianceSummary.infoCount,
                    })}
                  </button>
                </>
            )}
          </BannerText>
          <BannerClose onClick={() => setGenerateResult(null)} aria-label={t('common.cancel')}>
            <X size={16} />
          </BannerClose>
        </ResultBanner>
      )}

      {/* Filter row */}
      <FilterRow>
        <TextInput
          placeholder={t('shiftSchedule.filterName')}
          value={filterNameInput}
          onChange={(e) => setFilterNameInput(e.target.value)}
        />
        <AsyncGenericSelect
          label={t('shiftSchedule.filterDepartment')}
          compact
          value={filterDepartmentIds}
          onChange={setFilterDepartmentIds}
          onSearch={searchDepartments}
          isMulti
        />
        <AsyncGenericSelect
          label={t('shiftSchedule.filterUserGroup')}
          compact
          value={filterUserGroupIds}
          onChange={setFilterUserGroupIds}
          onSearch={searchUserGroups}
          isMulti
        />
      </FilterRow>

      {/* Grid */}
      <GridWrapper>
        <Grid>
          {/* Header row */}
          <HeaderCell>{t('shiftSchedule.employee')}</HeaderCell>
          {weekDays.map((day, i) => (
            <HeaderCell key={i} $isToday={formatDateISO(day) === todayStr}>
              {getDayLabel(day, i)}
            </HeaderCell>
          ))}

          {/* Loading state */}
          {loading && (
            <LoadingOverlay>{t('shiftSchedule.loading')}</LoadingOverlay>
          )}

          {/* Empty state */}
          {!loading && gridData.length === 0 && (
            <EmptyState>{t('shiftSchedule.noData')}</EmptyState>
          )}

          {/* Data rows */}
          {!loading && gridData.map((row) => (
            <React.Fragment key={row.userId}>
              <EmployeeCell>
                <PersonCell
                  name={row.userFullName}
                  profilePicture={row.userProfilePicture}
                />
                {row.rotationName ? (
                  <Tooltip
                    text={t('shiftSchedule.rotationBadgeTooltip', {
                      name: row.rotationName,
                      day: row.rotationStartDay,
                    })}
                    position="bottom"
                  >
                    <RotationBadge>{row.rotationName}</RotationBadge>
                  </Tooltip>
                ) : (
                  <NoRotationLabel>{t('shiftSchedule.noRotation')}</NoRotationLabel>
                )}
              </EmployeeCell>
              {weekDays.map((day, i) => {
                const dateKey = formatDateISO(day);
                const shift = row.days?.[dateKey];
                const isToday = dateKey === todayStr;
                const isLeave = !!shift?.leaveStatus;
                const cellClickable = canEditCell && !isLeave;
                const onCellClick = cellClickable
                  ? () => handleOpenPicker(row, day, shift)
                  : undefined;
                const cellViolations = violationsByCell[`${row.userId}_${dateKey}`];

                return (
                  <ShiftCell
                    key={dateKey}
                    $isToday={isToday}
                    $clickable={cellClickable}
                    onClick={onCellClick}
                  >
                    {renderDayContent(shift)}
                    {cellViolations && cellViolations.length > 0 && (
                      <ViolationBadgeAnchor>
                        <Tooltip text={cellViolations.map(renderComplianceMessage).join(' | ')} position="top">
                          <ViolationDot $color={getHighestComplianceSeverityColor(cellViolations)} />
                        </Tooltip>
                      </ViolationBadgeAnchor>
                    )}
                  </ShiftCell>
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
      </GridWrapper>

      {/* Grid pager — yalnız birden çok sayfa varsa görünür */}
      {gridTotal > GRID_PAGE_SIZE && (
        <PagerRow>
          <PagerText>
            {(gridPage - 1) * GRID_PAGE_SIZE + 1}
            {'–'}
            {Math.min(gridPage * GRID_PAGE_SIZE, gridTotal)} / {gridTotal}
          </PagerText>
          <Button
            variant="outline"
            onClick={() => setGridPage((p) => Math.max(p - 1, 1))}
            disabled={gridPage <= 1 || loading}
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outline"
            onClick={() => setGridPage((p) => p + 1)}
            disabled={gridPage * GRID_PAGE_SIZE >= gridTotal || loading}
          >
            {t('common.next')}
          </Button>
        </PagerRow>
      )}

      {/* Publish confirm */}
      <ConfirmDialog
        isOpen={publishConfirm}
        title={t('shiftSchedule.publishTitle')}
        message={t('shiftSchedule.publishMessage', {
          start: formatDateFull(currentWeekStart),
          end: formatDateFull(weekEnd),
        })}
        onConfirm={handlePublish}
        onCancel={() => setPublishConfirm(false)}
        loading={publishing}
      />

      {/* Copy confirm */}
      <ConfirmDialog
        isOpen={copyConfirm}
        title={t('shiftSchedule.copyTitle')}
        message={t('shiftSchedule.copyMessage', {
          source: formatDateFull(addDays(currentWeekStart, -7)),
          target: formatDateFull(currentWeekStart),
        })}
        onConfirm={handleCopy}
        onCancel={() => setCopyConfirm(false)}
        loading={copying}
      />

      {/* Generate Drawer */}
      <Drawer
        open={generateDrawerOpen}
        onClose={() => setGenerateDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: 380,
          margin: '12px',
          borderRadius: '6px',
          height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('shiftSchedule.generateTitle')}</DrawerTitle>
          <CloseBtn onClick={() => setGenerateDrawerOpen(false)}>
            <X size={20} />
          </CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          <FieldGroup>
            <FieldItem $span={12}>
              <ScopeInfoBox>
                <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  {scopeInfo
                    ? t('shiftSchedule.generateScopeInfo', {
                        rotation: scopeInfo.rotationUserCount,
                        plan: scopeInfo.planOnlyUserCount,
                      })
                    : t('shiftSchedule.generateScopeInfoLoading')}
                </span>
              </ScopeInfoBox>
            </FieldItem>
            <FieldItem $span={6}>
              <DateTimePicker
                label={t('shiftSchedule.startDate')}
                value={generateForm.startDate}
                onChange={(val) => setGenerateForm((p) => ({ ...p, startDate: val }))}
                mode="date"
                required
              />
            </FieldItem>
            <FieldItem $span={6}>
              <DateTimePicker
                label={t('shiftSchedule.endDate')}
                value={generateForm.endDate}
                onChange={(val) => setGenerateForm((p) => ({ ...p, endDate: val }))}
                mode="date"
                required
              />
            </FieldItem>
            <FieldItem $span={12}>
              <CheckboxInput
                label={t('shiftSchedule.overwriteExisting')}
                checked={generateForm.overwriteExisting}
                onChange={(val) => setGenerateForm((p) => ({ ...p, overwriteExisting: val }))}
              />
            </FieldItem>
            <FieldItem $span={12}>
              <AdvancedToggle type="button" onClick={() => setAdvancedOpen((p) => !p)}>
                {advancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {t('shiftSchedule.advancedScope')}
              </AdvancedToggle>
            </FieldItem>
            {advancedOpen && (
              <>
                <FieldItem $span={12}>
                  <AsyncGenericSelect
                    label={t('shiftSchedule.employees')}
                    value={generateForm.employeeIds}
                    onChange={(val) => setGenerateForm((p) => ({ ...p, employeeIds: val }))}
                    onSearch={searchEmployees}
                    renderOption={(opt) => <PersonCell name={opt.name || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()} profilePicture={opt.profilePicture} />}
                    isMulti
                  />
                </FieldItem>
                <FieldItem $span={12}>
                  <AsyncGenericSelect
                    label={t('shiftSchedule.departments')}
                    value={generateForm.departmentIds}
                    onChange={(val) => setGenerateForm((p) => ({ ...p, departmentIds: val }))}
                    onSearch={searchDepartments}
                    isMulti
                  />
                </FieldItem>
                <FieldItem $span={12}>
                  <AsyncGenericSelect
                    label={t('shiftSchedule.locations')}
                    value={generateForm.locationIds}
                    onChange={(val) => setGenerateForm((p) => ({ ...p, locationIds: val }))}
                    onSearch={searchLocations}
                    isMulti
                  />
                </FieldItem>
                <FieldItem $span={12}>
                  <AsyncGenericSelect
                    label={t('shiftSchedule.userGroups')}
                    value={generateForm.userGroupIds}
                    onChange={(val) => setGenerateForm((p) => ({ ...p, userGroupIds: val }))}
                    onSearch={searchUserGroups}
                    isMulti
                  />
                </FieldItem>
              </>
            )}
          </FieldGroup>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => setGenerateDrawerOpen(false)}>
            {t('shiftSchedule.cancel')}
          </Button>
          <Button onClick={handleGenerate} disabled={generating || !generateForm.startDate || !generateForm.endDate}>
            {generating ? t('shiftSchedule.generating') : t('shiftSchedule.generate')}
          </Button>
        </DrawerFooter>
      </Drawer>

      {/* Clear Drawer */}
      <Drawer
        open={clearDrawerOpen}
        onClose={() => setClearDrawerOpen(false)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: 380,
          margin: '12px',
          borderRadius: '6px',
          height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{t('shiftSchedule.clearTitle')}</DrawerTitle>
          <CloseBtn onClick={() => setClearDrawerOpen(false)}>
            <X size={20} />
          </CloseBtn>
        </DrawerHeader>

        <DrawerBody>
          <FieldGroup>
            <FieldItem $span={6}>
              <DateTimePicker
                label={t('shiftSchedule.startDate')}
                value={clearForm.startDate}
                onChange={(val) => setClearForm((p) => ({ ...p, startDate: val }))}
                mode="date"
                required
              />
            </FieldItem>
            <FieldItem $span={6}>
              <DateTimePicker
                label={t('shiftSchedule.endDate')}
                value={clearForm.endDate}
                onChange={(val) => setClearForm((p) => ({ ...p, endDate: val }))}
                mode="date"
                required
              />
            </FieldItem>
            <FieldItem $span={12}>
              <AsyncGenericSelect
                label={t('shiftSchedule.employees')}
                value={clearForm.employeeIds}
                onChange={(val) => setClearForm((p) => ({ ...p, employeeIds: val }))}
                onSearch={searchEmployees}
                renderOption={(opt) => <PersonCell name={opt.name || `${opt.firstName || ''} ${opt.lastName || ''}`.trim()} profilePicture={opt.profilePicture} />}
                isMulti
              />
            </FieldItem>
            <FieldItem $span={12}>
              <AsyncGenericSelect
                label={t('shiftSchedule.departments')}
                value={clearForm.departmentIds}
                onChange={(val) => setClearForm((p) => ({ ...p, departmentIds: val }))}
                onSearch={searchDepartments}
                isMulti
              />
            </FieldItem>
            <FieldItem $span={12}>
              <AsyncGenericSelect
                label={t('shiftSchedule.locations')}
                value={clearForm.locationIds}
                onChange={(val) => setClearForm((p) => ({ ...p, locationIds: val }))}
                onSearch={searchLocations}
                isMulti
              />
            </FieldItem>
            <FieldItem $span={12}>
              <AsyncGenericSelect
                label={t('shiftSchedule.userGroups')}
                value={clearForm.userGroupIds}
                onChange={(val) => setClearForm((p) => ({ ...p, userGroupIds: val }))}
                onSearch={searchUserGroups}
                isMulti
              />
            </FieldItem>
          </FieldGroup>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => setClearDrawerOpen(false)}>
            {t('shiftSchedule.cancel')}
          </Button>
          <Button onClick={handleClear} disabled={clearing || !clearForm.startDate || !clearForm.endDate}>
            {clearing ? t('shiftSchedule.clearing') : t('shiftSchedule.clear')}
          </Button>
        </DrawerFooter>
      </Drawer>

      <WorkHourPickerDrawer
        isOpen={!!pickerCtx}
        onClose={handleClosePicker}
        onSelect={handlePickWorkHour}
        currentWorkHourId={pickerCtx?.currentWorkHourId}
        subtitle={pickerCtx ? `${pickerCtx.userFullName} • ${pickerCtx.date}` : undefined}
        workHours={workHours}
        flags={[
          { key: 'isOvertime', label: t('shiftSchedule.isOvertimeShift'), initial: !!pickerCtx?.isOvertime },
          { key: 'isWeeklyRest', label: t('shiftSchedule.isWeeklyRest'), initial: !!pickerCtx?.isWeeklyRest },
        ]}
      />

      <ComplianceDrawer
        isOpen={complianceOpen}
        onClose={() => setComplianceOpen(false)}
        violations={complianceViolations}
        loading={complianceLoading}
      />

      <ComplianceSettingsModal
        isOpen={complianceSettingsOpen}
        onClose={() => setComplianceSettingsOpen(false)}
        onSaved={() => { if (complianceOpen) handleCheckCompliance(); }}
      />
    </Container>
  );
}
